import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowLeft, CheckCircle2, Trophy, XCircle } from "lucide-react";
import { EthiopianCross } from "@/components/EthiopianCross";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

export default function Lookup() {
  const [num, setNum] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    setError(""); setResult(null);
    const padded = num.trim().padStart(4, "0");
    if (!/^\d{4}$/.test(padded)) { setError("Enter a valid ticket number (e.g. 2001)."); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/tickets/lookup/${padded}`);
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Ticket not found.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] px-6 py-16 flex flex-col items-center">
      <div className="w-full max-w-lg">
        <Link to="/" className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#9E1B1B] transition-colors mb-8" data-testid="lookup-back"><ArrowLeft className="w-4 h-4" /> Back to Home</Link>
        <div className="text-center">
          <EthiopianCross className="w-24 h-24 mx-auto" />
          <h1 className="font-heading text-4xl font-bold mt-4">Ticket Lookup</h1>
          <p className="text-[#4A4A4A] mt-2">Verify any raffle ticket by its 4-digit number.</p>
        </div>

        <form onSubmit={search} className="mt-8 flex gap-3" data-testid="lookup-form">
          <Input value={num} onChange={(e) => setNum(e.target.value)} placeholder="e.g. 2001"
            className="text-center font-mono text-lg tracking-widest" data-testid="lookup-input" />
          <Button type="submit" disabled={loading} className="bg-[#9E1B1B] hover:bg-[#7A1414] text-white px-6" data-testid="lookup-submit">
            <Search className="w-5 h-5" />
          </Button>
        </form>

        {error && <p className="mt-4 text-center text-[#D00000] flex items-center justify-center gap-2" data-testid="lookup-error"><XCircle className="w-5 h-5" />{error}</p>}

        {result && (
          <div className="mt-8 bg-white border border-[#E2D9C8] rounded-2xl p-8 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="lookup-result">
            <div className="ticket-number text-4xl font-bold text-[#9E1B1B]">{result.ticket_number}</div>
            <div className="mt-4 inline-flex items-center gap-2 text-[#2D6A4F] font-semibold"><CheckCircle2 className="w-5 h-5" /> {result.status}</div>
            <p className="text-[#4A4A4A] mt-3">Ticket Holder: <strong>{result.holder}</strong></p>
            {result.is_winner && (
              <div className="mt-5 bg-[#C68E17] text-white rounded-lg py-4 px-6 flex items-center justify-center gap-2 font-semibold">
                <Trophy className="w-5 h-5" /> Winner — Prize #{result.prize_rank}!
              </div>
            )}
            <p className="text-xs text-[#4A4A4A] mt-4">Drawing Date: {result.drawing_date}</p>
          </div>
        )}
      </div>
    </div>
  );
}
